import { DateTime, Duration } from 'luxon';
import { ReactElement } from 'react';

import { VehicleClass } from 'dashboard/models/verification';
import { Paragraph } from 'design-system/components/Text';
import { ParagraphSize } from 'design-system/mixins/text';
import { TextColors } from 'design-system/theme';
import { COUNTRIES, CountryCode } from 'lib/constants/locales';
import {
  DATE_AND_TIME_FORMAT,
  HOURS_ELAPSED_FORMAT,
  MINUTES_SECONDS_ELAPSED_FORMAT,
  SECONDS_ELAPSED_FORMAT,
  SHORT_FORM_DATE,
  TIME_FORMAT_NO_SECONDS,
} from 'lib/constants/luxonFormats';
import { Iso8601DateString } from 'lib/types';

export const stripWhitespace = (value: string) => value.replace(/\s/g, '');

export const formatAddress = ({
  addressStreet1,
  addressStreet2,
  addressCity,
  addressSubdivision,
  addressPostalCode,
  key,
  color,
  size,
}: {
  addressStreet1?: string;
  addressStreet2?: string;
  addressCity?: string;
  addressSubdivision?: string;
  addressPostalCode?: string;
  countryCode?: string;
  key?: string;
  color?: keyof TextColors;
  size?: ParagraphSize;
}) => {
  if (addressStreet1 == null) {
    return null;
  }

  if (addressCity == null) {
    return <div>{addressStreet1}</div>;
  }

  return (
    <div key={key}>
      {addressStreet1 && (
        <Paragraph color={color} size={size}>
          {addressStreet1}
        </Paragraph>
      )}
      {addressStreet2 && (
        <Paragraph color={color} size={size}>
          {addressStreet2}
        </Paragraph>
      )}
      <Paragraph color={color} size={size}>
        {addressCity || '-'} {addressSubdivision || '-'}, {addressPostalCode || '-'}
      </Paragraph>
    </div>
  );
};

export const formatAddressFieldDisplay = ({
  addressStreet1,
  addressStreet2,
  addressCity,
  addressSubdivision,
  addressPostalCode,
  addressCountryCode,
}: {
  addressStreet1?: string;
  addressStreet2?: string;
  addressCity?: string;
  addressSubdivision?: string;
  addressPostalCode?: string;
  addressCountryCode?: CountryCode;
}): ReactElement | null => {
  if (
    addressStreet1 == null &&
    addressStreet2 == null &&
    addressCity == null &&
    addressSubdivision == null &&
    addressPostalCode == null &&
    addressCountryCode == null
  ) {
    return null;
  }

  return (
    <div>
      {addressStreet1 && <p>{addressStreet1}</p>}
      {addressStreet2 && <p>{addressStreet2}</p>}
      {addressCity && addressSubdivision && (
        <p>{`${addressCity}, ${addressSubdivision} ${addressPostalCode ?? ''}`.trim()}</p>
      )}
      {addressCity && !addressSubdivision && (
        <p>{`${addressCity} ${addressPostalCode ?? ''}`.trim()}</p>
      )}
      {!addressCity && (addressSubdivision || addressPostalCode) && (
        <p>{`${addressSubdivision ?? ''} ${addressPostalCode ?? ''}`.trim()}</p>
      )}
      {addressCountryCode && COUNTRIES[addressCountryCode] && (
        <p>{COUNTRIES[addressCountryCode]}</p>
      )}
    </div>
  );
};

export const formatVehicleClass = (vehicleClass: VehicleClass | undefined): ReactElement | null => {
  if (vehicleClass == null) {
    return null;
  }

  return (
    <div>
      <p>Issued: {formatDate(vehicleClass['issue-date'])}</p>
      <p>Expires: {formatDate(vehicleClass['expiration-date'])}</p>
    </div>
  );
};

export const formatNormalizedPostalCode = ({
  addressPostalCode,
  addressCountryCode,
}: {
  addressPostalCode?: string;
  addressCountryCode?: string;
}) => {
  let normalizedPostalCode = addressPostalCode;

  if (addressPostalCode && addressPostalCode.length === 9 && addressCountryCode === 'US') {
    if (addressPostalCode.slice(5, 9) === '0000') {
      normalizedPostalCode = addressPostalCode.slice(0, 5);
    } else {
      normalizedPostalCode = addressPostalCode.slice(0, 5) + '-' + addressPostalCode.slice(5, 9);
    }
  }

  return normalizedPostalCode;
};

export const formatDollar = (value: number, maximumFractionDigits = 2) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: maximumFractionDigits,
  }).format(value);

export const formatDate = (
  date: Iso8601DateString | null | undefined,
  format = SHORT_FORM_DATE,
) => {
  if (date == null || date === '') {
    return undefined;
  } else {
    return DateTime.fromISO(date).toFormat(format);
  }
};

export const formatBillingDate = (date: Iso8601DateString | undefined | null) => {
  if (date == null) {
    return undefined;
  } else {
    return DateTime.fromISO(date).toUTC().toFormat(SHORT_FORM_DATE);
  }
};

export const formatReportDates = (inputDate: string | undefined, desiredFormat: string) => {
  if (!inputDate) {
    return '';
  }
  // Case 1: YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(inputDate)) {
    return DateTime.fromFormat(inputDate, 'y-L-d').toFormat(desiredFormat);
  }
  // Case 2: YYYY-MM
  if (/^\d{4}-\d{2}-\d{2}$/.test(inputDate)) {
    return DateTime.fromFormat(inputDate, 'y-L').toFormat(desiredFormat);
  }
  // Case 3: YYYY
  if (/^\d{4}$/.test(inputDate)) {
    return DateTime.fromFormat(inputDate, 'y').toFormat(desiredFormat);
  }
  return inputDate;
};

/**
 * Formats labels
 */
export const formatChartDate = (timestamp: any) => {
  return typeof timestamp == 'number'
    ? DateTime.fromSeconds(timestamp).toFormat('M/d')
    : DateTime.local().toFormat('M/d');
};

export const formatChartDateHourChart = (timestamp: any) => {
  if (typeof timestamp == 'number') {
    const hour = DateTime.fromSeconds(timestamp).toFormat('hh');
    // When we're at an interval of 3, print the Hour to prevent crowding
    return parseInt(hour) % 3 === 0
      ? DateTime.fromSeconds(timestamp).toFormat(parseInt(hour) >= 10 ? 'hh a' : 'h a')
      : '';
  }
  return timestamp;
};
export const formatChartDateLong = (timestamp: any) => {
  if (typeof timestamp == 'number') {
    return DateTime.fromSeconds(timestamp).toFormat('MMM d, y');
  }
  return timestamp;
};
export const formatChartDateMonth = (timestamp: any) => {
  if (typeof timestamp == 'number') {
    return DateTime.fromSeconds(timestamp).toFormat('MMM y');
  }
  return timestamp;
};
export const formatChartMonthName = (timestamp: any) => {
  if (typeof timestamp == 'number') {
    return DateTime.fromSeconds(timestamp).toFormat('MMM');
  }
  return timestamp;
};
export const formatChartDateHour = (timestamp: any) => {
  if (typeof timestamp == 'number') {
    return DateTime.fromSeconds(timestamp).toFormat('ff');
  }
  return timestamp;
};

export const formatChartDateLongWithDay = (timestamp: any) => {
  if (typeof timestamp == 'number') {
    return DateTime.fromSeconds(timestamp).toFormat('DDD');
  }
  return timestamp;
};

export const formatNumber = (value: number, opts?: Intl.NumberFormatOptions) =>
  new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    ...opts,
  }).format(value);

/**
 * This function intentionally loses precision. It is intended to be used for
 * large round numbers.
 *
 * Intentionally give 4 + 5 digit numbers 1 decimal precision when non integer
 */
export const formatNumberLarge = (value: number): string => {
  const absValue = Math.abs(value);
  const sign = Math.sign(value);
  const rawValueK = (absValue / 10 ** 3) * sign;

  if (absValue < 10 ** 3) {
    return `${absValue * sign}`;
  } else if (absValue < 10 ** 4) {
    return rawValueK % 1 === 0 ? `${rawValueK}K` : `${rawValueK.toPrecision(2)}K`;
  } else if (absValue < 10 ** 5) {
    return rawValueK % 1 === 0 ? `${rawValueK}K` : `${rawValueK.toPrecision(3)}K`;
  } else if (absValue < 10 ** 6) {
    return `${Math.round(absValue / 10 ** 3) * sign}K`;
  } else if (absValue < 10 ** 9) {
    return `${Math.round(absValue / 10 ** 6) * sign}M`;
  } else if (absValue < 10 ** 12) {
    return `${Math.round(absValue / 10 ** 9) * sign}B`;
  } else {
    return `${Math.round(absValue / 10 ** 12) * sign}T`;
  }
};

/**
 * Formats date for start and end of date range (generally for filtering purposes))
 */
export function formatDateRangeStart(date?: Iso8601DateString | DateTime): Iso8601DateString {
  if (date == null) {
    return DateTime.local().startOf('day').toISO();
  }
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return typeof date == 'string'
    ? DateTime.fromISO(date).startOf('day').toISO()
    : date.startOf('day').toISO();
}

export function formatDateRangeEnd(date?: Iso8601DateString | DateTime): Iso8601DateString {
  if (date == null) {
    return DateTime.local().endOf('day').toISO();
  }
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return typeof date == 'string'
    ? DateTime.fromISO(date).endOf('day').toISO()
    : date.endOf('day').toISO();
}

/**
 * Gets a formatted completion time and time range from any entity with
 * created-at and completed-at attributes.
 */
export function formatTimeRangeDuration(
  start: Iso8601DateString,
  end?: Iso8601DateString,
): { completionTime: string | undefined; timeRange: string } {
  let completionTime: string | undefined;
  let timeRange: string;
  const startDate = DateTime.fromISO(start);

  if (end != null) {
    const endDate = DateTime.fromISO(end);
    const createdAtFormatted = startDate.toFormat(TIME_FORMAT_NO_SECONDS);
    const completedAtFormatted = endDate.toFormat(TIME_FORMAT_NO_SECONDS);

    timeRange = `${createdAtFormatted} - ${completedAtFormatted}`;
    const completionTimeSeconds = endDate.diff(startDate, 'seconds').seconds;
    const duration = Duration.fromObject({ seconds: completionTimeSeconds });
    completionTime = duration.toFormat(
      completionTimeSeconds >= 60 * 60
        ? HOURS_ELAPSED_FORMAT
        : completionTimeSeconds >= 60
          ? MINUTES_SECONDS_ELAPSED_FORMAT
          : SECONDS_ELAPSED_FORMAT,
    );
  } else {
    timeRange = startDate.toFormat(TIME_FORMAT_NO_SECONDS);
  }

  return { completionTime, timeRange };
}

export function formatDateTimeRangeDuration(
  start: Iso8601DateString,
  end?: Iso8601DateString,
): { completionTime: string | undefined; dateTimeRange: string } {
  let completionTime: string | undefined;
  let dateTimeRange: string;
  const startDate = DateTime.fromISO(start);

  if (end != null) {
    const endDate = DateTime.fromISO(end);
    const createdAtFormatted = startDate.toFormat(DATE_AND_TIME_FORMAT);
    const completedAtFormatted = endDate.toFormat(DATE_AND_TIME_FORMAT);

    dateTimeRange = `${createdAtFormatted} - ${completedAtFormatted}`;
    const completionTimeSeconds = endDate.diff(startDate, 'seconds').seconds;
    const duration = Duration.fromObject({ seconds: completionTimeSeconds });
    completionTime = duration.toFormat(
      completionTimeSeconds >= 60 * 60
        ? HOURS_ELAPSED_FORMAT
        : completionTimeSeconds >= 60
          ? MINUTES_SECONDS_ELAPSED_FORMAT
          : SECONDS_ELAPSED_FORMAT,
    );
  } else {
    dateTimeRange = startDate.toFormat(DATE_AND_TIME_FORMAT);
  }

  return { completionTime, dateTimeRange };
}

export function formatBytes(bytes: number): string {
  const units = ['bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  let unitIndex = 0;
  while (bytes >= 1000) {
    ++unitIndex;
    bytes = bytes / 1000;
  }

  return bytes.toFixed(bytes < 10 && unitIndex > 0 ? 1 : 0) + ' ' + units[unitIndex];
}
